// modules/approvals/hooks/useDecisionForm.js
//
// Loads the extra input a decision needs at decision time — today, the bank UTRs
// an approver supplies when approving a payment, which could not exist when the
// maker recorded it. Shared by every approve modal so the rule lives in one place.
//
// Returns nulls for entity types that need nothing, which is every type except
// PAYMENT_APPROVAL, so a screen can wire this in unconditionally.

import { useState, useEffect } from 'react';
import { fetchDecisionForm } from '../api';
import { validateSplits } from '../components/ApproverPaymentSplits';

export default function useDecisionForm(instanceId, { enabled = true } = {}) {
  const [form, setForm] = useState(null);
  const [splits, setSplits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !instanceId) {
      setForm(null);
      setSplits([]);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDecisionForm(instanceId)
      .then((data) => {
        if (cancelled) return;
        if (data && data.kind === 'PAYMENT_SPLITS') {
          setForm(data);
          setSplits((data.splits || []).map((s) => ({ ...s })));
        } else {
          setForm(null);
          setSplits([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Never block a decision on this lookup — the server re-validates on
        // approve and refuses if a UTR is genuinely missing.
        setError(err?.response?.data?.message || 'Could not load the payment breakdown.');
        setForm(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [instanceId, enabled]);

  const errors = form ? validateSplits(splits, form) : [];

  return {
    form,
    splits,
    setSplits,
    loading,
    error,
    errors,
    // Approving is blocked while the breakdown is incomplete.
    blocked: !!form && errors.length > 0,
    // What to send as the decision payload, or undefined when nothing is needed.
    payload: form ? { paymentSplits: splits } : undefined,
  };
}
